import { makeStyles } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import { useUserProfile } from '@backstage/plugin-user-settings';
import { CustomPageLayout } from '../common/CustomPageLayout';
import { getGreetingBasedOnTimeOfDay } from '../../content/greetings';
import { LinkCard } from '../common/LinkCard';
import { GitHubIcon, DevBoxIcon, ClaudeIcon } from '../common/ToolIcons';
import ArlaLogo from '../../../public/logos/Arla_Logo_Primary_RGB.png';

// ---------------------------------------------------------------------------
// Icons
// Each icon is self-contained so LinkCard stays layout-only.
// Action card icons include the gray background circle (iconSize 32).
// Tool icons are plain SVG / img (iconSize 28).
// ---------------------------------------------------------------------------

const CreateIcon = () => (
  <div
    style={{
      width: 32,
      height: 32,
      borderRadius: '50%',
      backgroundColor: '#f5f5f5',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <AddIcon style={{ color: '#163a66', fontSize: 20 }} />
  </div>
);

const ArlaLogoIcon = () => (
  <img
    src={ArlaLogo}
    alt=""
    aria-hidden="true"
    style={{ width: 28, height: 'auto', objectFit: 'contain' }}
  />
);


// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const useStyles = makeStyles({
  body: {
    backgroundColor: 'var(--arla-page-content-bg)',
    padding: '24px 24px 48px',
    flex: 1,
    minHeight: '100%',
  },
  cardsRow: {
    display: 'flex',
    gap: '32px',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
  },
  leftCardWrap: {
    flexShrink: 0,
    maxWidth: '100%',
  },
  rightCardWrap: {
    flex: 1,
    // When the right card can no longer fit at 300 px it wraps below and
    // takes the full available width thanks to flex: 1 on a new row.
    minWidth: '300px',
  },
  card: {
    backgroundColor: 'var(--arla-card-bg)',
    borderRadius: '4px',
    boxShadow:
      '0px 0px 4px rgba(0,0,0,0.25), 0px 1px 1px rgba(0,0,0,0.14), 0px 2px 1px rgba(0,0,0,0.12)',
  },
  cardHeader: {
    padding: '24px 20px',
    borderBottom: '1px solid #d9d9d9',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  cardTitle: {
    fontFamily: 'Roboto, sans-serif',
    fontSize: '20px',
    fontWeight: 600,
    lineHeight: 1.4,
    color: 'var(--arla-text-primary-color)',
    margin: 0,
  },
  cardSubtitle: {
    fontFamily: 'Roboto, sans-serif',
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: 1.4,
    color: '#757575',
    margin: 0,
  },
  cardBody: {
    padding: '24px 20px',
  },
  linkCardsRow: {
    display: 'grid',
    // auto auto: each column is sized by its own content, which drives the
    // card width outward — no circular dependency with the container.
    // The media query switches to single column only when the viewport is
    // narrow enough that the two cards would genuinely overflow the card.
    gridTemplateColumns: 'auto auto',
    gap: '12px',
    '@media (max-width: 800px)': {
      gridTemplateColumns: '1fr',
    },
  },
  linkCardsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
});

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const extractFirstName = (displayName: string | undefined): string => {
  if (!displayName) return '';
  const parts = displayName.trim().split(/\s+/);
  return parts[0] || displayName;
};


export const CardFrontPage = () => {
  const classes = useStyles();
  const { displayName } = useUserProfile();
  const firstName = extractFirstName(displayName);
  const greeting = getGreetingBasedOnTimeOfDay();
  const greetingFull = firstName ? `${greeting}, ${firstName}` : greeting;

  return (
    <CustomPageLayout
      title={greetingFull}
      breadcrumbs={[{ label: 'Home' }]}
      showDate={false}
    >
      <div className={classes.body}>
        <div className={classes.cardsRow}>
          {/* ---- Create components ---- */}
          <div className={classes.leftCardWrap}>
            <div className={classes.card}>
              <div className={classes.cardHeader}>
                <p className={classes.cardTitle}>Create components</p>
                <p className={classes.cardSubtitle}>
                  Add software your teams own to the catalog
                </p>
              </div>
              <div className={classes.cardBody}>
                <div className={classes.linkCardsRow}>
                  <LinkCard
                    label="Create new component"
                    sublabel="From available templates"
                    icon={<CreateIcon />}
                    iconSize={32}
                    to="/create"
                    primary
                  />
                  <LinkCard
                    label="Register existing component"
                    sublabel="Add a repo already on GitHub"
                    icon={<CreateIcon />}
                    iconSize={32}
                    to="/catalog-import"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* ---- Docs for developer tools ---- */}
          <div className={classes.rightCardWrap}>
            <div className={classes.card}>
              <div className={classes.cardHeader}>
                <p className={classes.cardTitle}>Docs for developer tools</p>
              </div>
              <div className={classes.cardBody}>
                <div className={classes.linkCardsList}>
                  <LinkCard
                    label="GitHub"
                    sublabel="Source control"
                    icon={<GitHubIcon />}
                    iconSize={28}
                    to="/docs/default/component/github-governance"
                  />
                  <LinkCard
                    label="Dev Box"
                    sublabel="Cloud workstation"
                    icon={<DevBoxIcon />}
                    iconSize={28}
                    to="/docs/default/component/dev-box-user-docs"
                  />
                  <LinkCard
                    label="The Developer Platform"
                    sublabel="Overview of the Developer Platform"
                    icon={<ArlaLogoIcon />}
                    iconSize={28}
                    to="/docs/default/system/internal-developer-platform"
                  />
                  <LinkCard
                    label="Claude"
                    sublabel="Claude docs and guidelines"
                    icon={<ClaudeIcon />}
                    iconSize={28}
                    to="/docs/default/component/claude-user-docs"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </CustomPageLayout>
  );
};
